import React, { useState, useEffect } from 'react';
import { auth, db } from '../firebaseConfig';
import { collection, query, where, getDocs, doc, getDoc, Timestamp } from 'firebase/firestore';
import Post from './Post';

const CaughtDreams = () => {
  const [caughtDreams, setCaughtDreams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchCaughtDreams = async () => {
      if (!auth.currentUser) return;

      try {
        // Get user's profile to get caught dreams
        const userProfileRef = doc(db, 'profiles', auth.currentUser.uid);
        const userProfileDoc = await getDoc(userProfileRef);
        const caughtDreamIds = userProfileDoc.data()?.caughtDreams || [];

        // Get all caught dreams
        const posts = [];
        for (const postId of caughtDreamIds) {
          const postDoc = await getDoc(doc(db, 'posts', postId));
          if (postDoc.exists()) {
            const postData = postDoc.data();
            
            // Get author data
            const authorDoc = await getDoc(doc(db, 'profiles', postData.userId));
            const authorData = authorDoc.exists() ? authorDoc.data() : null;
            
            // Ensure createdAt is a proper timestamp
            let createdAt = postData.createdAt;
            if (createdAt) {
              if (createdAt instanceof Timestamp) {
                // Already a Firestore Timestamp
                createdAt = createdAt;
              } else if (createdAt.seconds) {
                // Convert to Firestore Timestamp
                createdAt = new Timestamp(createdAt.seconds, createdAt.nanoseconds || 0);
              } else if (typeof createdAt === 'string') {
                // Convert string to Timestamp
                createdAt = Timestamp.fromDate(new Date(createdAt));
              } else {
                // Default to current time if invalid
                createdAt = Timestamp.now();
              }
            } else {
              // If no timestamp, use current time
              createdAt = Timestamp.now();
            }
            
            posts.push({
              id: postDoc.id,
              ...postData,
              createdAt,
              author: authorData
            });
          }
        }

        // Sort posts by most recently caught
        posts.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date();
          const dateB = b.createdAt?.toDate?.() || new Date();
          return dateB - dateA;
        });
        
        setCaughtDreams(posts);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching caught dreams:', error);
        setError('Failed to load caught dreams. Please try again later.');
        setLoading(false);
      }
    };

    fetchCaughtDreams();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 dark:border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8 text-center">
        <p className="text-red-500 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <h2 className="text-2xl font-bold mb-6 text-gray-900 dark:text-white">Caught Dreams</h2>
      
      {caughtDreams.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-600 dark:text-gray-400">
            You haven't caught any dreams yet.
          </p>
          <p className="text-sm text-gray-500">
            Catch posts to see them appear here!
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {caughtDreams.map(post => (
            <Post key={post.id} post={post} />
          ))}
        </div>
      )}
    </div>
  );
};

export default CaughtDreams;
